import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../lib/session";
import { allSocieties, listPlatformAdmins } from "../lib/api";
import { PageHead, Loading, Notice } from "../components/ui";
import Back from "../components/Back";

/**
 * Whoever runs the site. Every society, who's in them, and who else holds
 * this level of access.
 *
 * A platform admin passes every society check in the database, so this
 * page is only a window onto an access level that already exists — it
 * isn't what grants it. Keep the list of platform admins short, and don't
 * use this to read a society's scores out of idle curiosity: it's their
 * data, not yours.
 */
export default function Platform() {
  const { isPlatformAdmin, profile, society, chooseSociety } = useSession();
  const nav = useNavigate();
  const [socs, setSocs] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isPlatformAdmin) return;
    Promise.all([allSocieties(), listPlatformAdmins()])
      .then(([s, a]) => { setSocs(s); setAdmins(a); })
      .catch((e) => setError(e.message));
  }, [isPlatformAdmin]);

  if (!isPlatformAdmin) {
    return <Notice kind="error">That page is for site administrators.</Notice>;
  }
  if (error) return <Notice kind="error">{error}</Notice>;
  if (!socs) return <Loading />;

  const count = (rel) => (Array.isArray(rel) ? rel[0]?.count ?? 0 : 0);

  return (
    <>
      <Back to="/society">Society</Back>
      <PageHead title="Site administration"
                sub={`${socs.length} societ${socs.length === 1 ? "y" : "ies"}`} />

      <div className="notice notice-info" style={{ marginBottom: "1.25rem" }}>
        You pass every permission check in every society. That's what makes
        this page work, and why it's worth being careful with it.
      </div>

      <h2 style={{ fontSize: ".9375rem", marginBottom: ".6rem" }}>Societies</h2>
      <div className="list" style={{ "--cols": "minmax(0,1fr) 4rem 4rem 4.5rem", marginBottom: "1.5rem" }}>
        <div className="list-row head">
          <span>Name</span>
          <span style={{ textAlign: "right" }}>Members</span>
          <span style={{ textAlign: "right" }}>Comps</span>
          <span style={{ textAlign: "right" }}>Open</span>
        </div>
        {socs.map((s) => (
          <div className="list-row" key={s.id}>
            <span style={{ minWidth: 0 }}>
              <strong style={{ display: "block", overflow: "hidden",
                               textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {s.name}
              </strong>
              <small className="muted" style={{ fontSize: ".6875rem" }}>
                since {new Date(s.created_at).toLocaleDateString("en-GB",
                  { month: "short", year: "numeric" })}
                {s.allow_code_signup && " · open sign-up"}
              </small>
            </span>
            <span className="fig" style={{ textAlign: "right" }}>
              {count(s.society_members)}
            </span>
            <span className="fig" style={{ textAlign: "right" }}>
              {count(s.competitions)}
            </span>
            {/* The only action a site administrator needs here: go and
                stand in a society. You pass every check inside it, so
                fixing something means being in it, not having a row of
                remote-control buttons on this page. */}
            <span style={{ textAlign: "right" }}>
              {s.id === society?.id ? (
                <span className="pill pill-mark">you're in</span>
              ) : (
                <button className="btn-link"
                        onClick={() => {
                          chooseSociety(s.id, socs.map((x) => ({ ...x, role: "admin" })));
                          nav("/");
                        }}>Open</button>
              )}
            </span>
          </div>
        ))}
      </div>

      <h2 style={{ fontSize: ".9375rem", marginBottom: ".6rem" }}>Site administrators</h2>
      <div className="list">
        {admins.map((a) => (
          <div className="list-row" key={a.profile_id}>
            <span style={{ minWidth: 0 }}>
              <strong style={{ display: "block" }}>
                {a.profiles?.full_name ?? "unknown"}
                {a.profile_id === profile?.id && " (you)"}
              </strong>
              <small className="muted" style={{ fontSize: ".6875rem" }}>
                {a.profiles?.email} {a.note && `· ${a.note}`}
              </small>
            </span>
            <span className="pill pill-live">platform</span>
          </div>
        ))}
      </div>
      <p className="muted" style={{ fontSize: ".75rem", marginTop: ".6rem", lineHeight: 1.6 }}>
        Appointing another one is deliberately a database job, not a button —
        insert into <code>platform_admins</code>. It's the one privilege
        nobody should be able to hand out by accident.
      </p>
    </>
  );
}
